import React, { useEffect, useState } from "react";
import { Radio, MapPin, AlertTriangle, X, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * LiveVisitTracker — persistent overlay for the provider's mobile screen while
 * a visit is in progress. Presentational only; the tracking state is owned by
 * the parent (usually via the `useVisitTracking` hook) and passed through.
 *
 * Props:
 *   tracking (object, required) — the return value of `useVisitTracking()`
 *   onEndVisit (fn) — called AFTER the tracking session is closed; parent
 *                     typically calls the backend "Complete Visit" here.
 */
export default function LiveVisitTracker({ tracking, onEndVisit }) {
  const {
    outside = false,
    distanceM = 0,
    starting = false,
    ending = false,
    endTracking,
    isTracking = false,
  } = tracking || {};
  const [ackedOutside, setAckedOutside] = useState(false);
  useEffect(() => { if (!outside) setAckedOutside(false); }, [outside]);

  const handleEnd = async () => {
    try { await endTracking?.(); } catch { /* fall through */ }
    onEndVisit?.();
  };

  if (!isTracking && !starting) return null;

  const distanceText = distanceM >= 1000
    ? `${(distanceM / 1000).toFixed(2)} km`
    : `${Math.round(distanceM)} m`;

  return (
    <>
      {/* Persistent "Live Tracking Active" indicator (mobile-first bottom bar) */}
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-40 max-w-md ml-auto"
        data-testid="live-tracking-bar"
      >
        <div className={`hah-card px-3 py-2.5 shadow-xl border-l-4 ${outside ? "border-l-[#EF4444] bg-[#FEF2F2]" : "border-l-[#10B981] bg-white"}`}>
          <div className="flex items-center gap-2.5">
            <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${outside ? "bg-[#EF4444]" : "bg-[#10B981]"}`}>
              <span className={`absolute inline-flex w-full h-full rounded-full opacity-70 animate-ping ${outside ? "bg-[#EF4444]" : "bg-[#10B981]"}`}></span>
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#1F2937] flex items-center gap-1.5">
                <Radio className="w-3 h-3" />
                <span data-testid="live-tracking-status">
                  {starting ? "Starting live tracking…" : "Live Tracking Active"}
                </span>
              </div>
              <div className="text-[11px] text-[#6B7280] flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                <span data-testid="live-tracking-distance">
                  {distanceText} from visit site
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412] h-8"
              onClick={handleEnd}
              disabled={ending}
              data-testid="live-tracking-end-visit"
            >
              <StopCircle className="w-3.5 h-3.5 mr-1" /> End Visit
            </Button>
          </div>
        </div>
      </div>

      {/* Persistent alert — re-appears if provider stays outside the geofence */}
      <Dialog open={outside && !ackedOutside} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm border-[#EF444433]"
          data-testid="live-tracking-away-alert"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#B91C1C]">
              <AlertTriangle className="w-5 h-5" /> Are you still at the visit?
            </DialogTitle>
            <DialogDescription>
              Our system detected you're <strong>{distanceText}</strong> away from the anchored
              visit site. If you've finished the visit, please tap <strong>End Visit</strong> below
              so live tracking stops and your dispatcher knows you're free.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button
              variant="outline"
              className="border-[#E5E7EB]"
              onClick={() => setAckedOutside(true)}
              data-testid="live-tracking-away-dismiss"
            >
              <X className="w-4 h-4 mr-1" /> I'm still on visit
            </Button>
            <Button
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
              onClick={handleEnd}
              disabled={ending}
              data-testid="live-tracking-away-end-visit"
            >
              <StopCircle className="w-4 h-4 mr-1" /> End Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
